import type { SystemDashboardResponse } from '@brickup/shared/dto'
import { MCP } from '@brickup/shared/locales/de/mcp'
import { NAV } from '@brickup/shared/locales/de/nav'
import { STAFF_DASHBOARD } from '@brickup/shared/locales/de/staff-dashboard'
import { projectsLink } from './dashboard-links'
import {
  DashboardData,
  DashboardPanel,
  DashboardRow,
  WindowBadge,
} from './DashboardLayout'
import { SystemHealth } from './SystemHealth'
import { GLANCE } from '@/api/glance'
import { useMcpConnections } from '@/api/mcp'
import { useSystemDashboard } from '@/api/staff-dashboard'
import { QueryGate } from '@/components/Internal/QueryGate'
import { RankedList } from '@/components/Internal/RankedList'
import { McpConnectionList } from '@/components/Internal/System/McpConnectionList'
import { LinkButton } from '@/components/LinkButton'

const COPY = STAFF_DASHBOARD.system

/**
 * What an administrator watches: whether the system is well, who is changing
 * the catalogue, how the work is spread, and which assistants are connected.
 */
export function SystemDashboard() {
  const query = useSystemDashboard()
  return (
    <DashboardData query={query} above={<SystemHealth />}>
      {data => <System data={data} />}
    </DashboardData>
  )
}

function System({ data }: { data: SystemDashboardResponse }) {
  return (
    <DashboardRow columns="thirds">
      <DashboardPanel
        title={COPY.teamActivity}
        subtitle={COPY.teamActivitySubtitle}
        badge={<WindowBadge />}
        action={
          <LinkButton
            to="/internal/system/operations/history"
            variant="link"
            size="sm"
          >
            {NAV.changeHistory}
          </LinkButton>
        }
      >
        <RankedList
          items={data.teamActivity.map((row, index) => ({
            key: `${index}`,
            label: row.name ?? COPY.deletedAccount,
            value: row.changes,
          }))}
          format={COPY.changes}
          emptyTitle={COPY.noTeamActivity}
        />
      </DashboardPanel>

      <DashboardPanel
        title={COPY.advisorLoad}
        subtitle={STAFF_DASHBOARD.activeProjects}
      >
        <RankedList
          items={data.advisorLoad.map(row => ({
            key: String(row.userId),
            label: row.name ?? COPY.noAdvisor,
            value: row.projects,
            to: projectsLink({ advisor: row.userId }),
          }))}
          emptyTitle={COPY.noAdvisorLoad}
        />
      </DashboardPanel>

      <AssistantConnections />
    </DashboardRow>
  )
}

/**
 * Read from the endpoint the System page serves, like the health tiles above
 * — the dashboard counts only what no page already answers.
 *
 * Reported here and cut there: a glance surface is not where an access is
 * taken away, so the panel offers the way to the page instead of the button.
 */
function AssistantConnections() {
  const query = useMcpConnections(GLANCE)

  return (
    <DashboardPanel
      title={MCP.connections.title}
      subtitle={MCP.connections.subtitle}
      action={
        <LinkButton
          to="/internal/system/administration/mcp"
          variant="link"
          size="sm"
        >
          {NAV.mcp}
        </LinkButton>
      }
    >
      <QueryGate
        query={query}
        loading="inline"
        error={MCP.connections.loadFailed}
      >
        {connections => <McpConnectionList connections={connections} />}
      </QueryGate>
    </DashboardPanel>
  )
}
